import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Brain, Briefcase } from 'lucide-react';
import { useStudentStats } from './../cases/hooks/use-cases';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { data: statsResponse, isLoading } = useStudentStats();
  const stats = statsResponse?.data;

  const casesAttempted = stats?.casesAttempted ?? 0;
  const responsesSubmitted = stats?.responsesSubmitted ?? 0;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Vet Collab Clinical Learning</h1>
        <p className="text-gray-600">Learn from real veterinary cases, attempt clinical thinking, and get noticed by practicing veterinarians.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {/* Explore Cases Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-start hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/feed')}>
          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
            <FileText className="text-blue-500" size={24} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Explore Cases</h2>
          <p className="text-gray-600 mb-6 text-sm flex-grow">Browse real clinical cases shared by veterinarians. Read summaries and view full case details.</p>
          <button className="text-blue-600 font-medium text-sm flex items-center hover:text-blue-700">
            Start Exploring <span className="ml-1">→</span>
          </button>
        </div>

        {/* Try a Case Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-start hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/challenges')}>
          <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
            <Brain className="text-purple-500" size={24} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Try a Case</h2>
          <p className="text-gray-600 mb-6 text-sm flex-grow">Think like a veterinarian. Work through real clinical scenarios and submit your diagnostic approach. Compare your thinking with real-world cases.</p>
          <button className="text-purple-600 font-medium text-sm flex items-center hover:text-purple-700">
            Start a Case Challenge <span className="ml-1">→</span>
          </button>
        </div>

        {/* Opportunities Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-start hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/opportunities')}>
          <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-6">
            <Briefcase className="text-green-500" size={24} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Opportunities</h2>
          <p className="text-gray-600 mb-6 text-sm flex-grow">Apply to clinics evaluating candidates through real case responses. Show your clinical thinking—not just your resume.</p>
          <button className="text-blue-600 font-medium text-sm flex items-center hover:text-blue-700">
            View Opportunities <span className="ml-1">→</span>
          </button>
        </div>
      </div>

      {/* Your Progress Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Your Progress</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {isLoading ? <span className="text-gray-300 text-lg">...</span> : casesAttempted}
              </div>
              <div className="text-sm text-gray-500">Cases Attempted</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {isLoading ? <span className="text-gray-300 text-lg">...</span> : responsesSubmitted}
              </div>
              <div className="text-sm text-gray-500">Responses Submitted</div>
            </div>
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="bg-purple-50 rounded-xl border border-purple-100 p-6 text-center">
        <p className="text-sm text-purple-900">
          <span className="font-semibold mr-1">New to clinical case discussions?</span> 
          Start by exploring real cases to understand how veterinarians think, then attempt a case to apply your approach.
        </p>
      </div>
    </div>
  );
};

export default StudentDashboard;
